import * as React from "react";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Urls } from "../../../../../Urls";
import { Navigator } from "../../../../../../Shared/Wizards/Entities/Navigator";
import { OrderSetupReview } from "../../../Common/Review/Component/OrderSetupReview";
import { ITableConfig, Table } from "../../../../../../Shared/Table/Table";
import { Order } from "../../../../../../../Wrap/Models/Domains/Orders/Order";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CurrencyCode } from "../../../../../../../Wrap/Constants/Enums/Domain/CurrencyCode";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { Typography } from "../../../../../../Shared/Typography/Typography";
import { getQueryParameters } from "../../../../../../Shared/Wizards/Entities/QueryParametersHelper";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { equlityBondWarning, hasDecisionMaker } from "../../Resource/Content";
import { DocumentsHelper } from "../../../PortfolioDashboard/Components/Document/Resource/DocumentsHelper";
import { DocumentTaskRequestTypeEnum } from "../../../../../../../Wrap/Constants/Enums/Tasks/DocumentTaskRequestTypeEnum";
import { DownloadButton } from "../../../../../../Shared/DownloadButton/DownloadButton";
import { CommissionChargeCancelMsg } from "../../../Common/AdviserCharges/Components/CommissionChargeCancelMsg";
import { ExternalFundDocument } from "../../../Common/Documents/Components/ExternalFundDocument";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { CashProductDeclarations } from "../../../Common/Review/Component/CashProductDeclarations";

interface IReviewProps extends IProcessComponentProps {
    type: SubAccountType;
    onClearError: VoidFunction;
}

interface IReviewState {
    isCashDeclarationChecked: boolean | null;
    isDocLoading: boolean;
}

export class Review extends ProcessComponent<IReviewProps, IReviewState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isDocLoading: false,
            isCashDeclarationChecked: false
        }
    }

    private getSellTableColumn() {
        const tableColConfig: ITableConfig[] = [
            {
                title: "Investment",
                key: "Instrument.DisplayName",
                render: (data, row) => {
                    return (
                        <>
                            {row.Instrument.FinexFactSheetUrl ? (
                                <a href={row.Instrument.FinexFactSheetUrl} target="_blank">
                                    {row.Instrument.Name || data}
                                </a>
                            ) : (
                                <p className="mb-0">{row.Instrument.Name || data}</p>
                            )}
                            {row.Instrument.DisplayName.toLowerCase() != "GBP Cash".toLowerCase() &&
                            <p className="mb-0">{row.Instrument.ProductCode || row.Instrument.InstrumentCode}</p>}
                        </>
                    );
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "Current units held",
                key: "Instrument.CurrQuantity",
                render: (data) => {
                    return data.toFixed(4);
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Current estimated value",
                key: "Instrument.MarketValue",
                render: (data, row: Order) => {
                    return MoneyDisplayHelper.getMoneyFormat(data);
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
            {
                title: "Estimated value to sell",
                key: "OrderValue",
                render: (data, row: Order) => {
                    return MoneyDisplayHelper.getMoneyFormat({
                        Amount: Number(data.toFixed(2)),
                        Currency: CurrencyCode.GBP,
                    });
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
        ];

        return tableColConfig;
    }

    handleCashDeclarationChange = (event) => {
        this.setState({ isCashDeclarationChecked: event });
      };

    private getBuyTableColumn() {
        const tableColConfig: ITableConfig[] = [
            {
                title: "Investment",
                key: "Instrument.DisplayName",
                render: (data, row) => {
                    return (
                        <>
                            {row.Instrument.FinexFactSheetUrl ? (
                                <a href={row.Instrument.FinexFactSheetUrl} target="_blank">
                                    {row.Instrument.Name || data}
                                </a>
                            ) : (
                                <p className="mb-0">{row.Instrument.Name || data}</p>
                            )}
                            {(row.Instrument.DisplayName || row.Instrument.Name).toLowerCase() != "GBP Cash".toLowerCase() &&
                            <p className="mb-0">{row.Instrument.ProductCode || row.Instrument.InstrumentCode}</p>}
                        </>
                    );
                },
                thCssWrapper: "cell-left",
                tdCssWrapper: "cell-left",
            },
            {
                title: "% Allocation",
                key: "OrderPercentage",
                render: (data) => {
                    return `${data || 0}%`;
                },
                thCssWrapper: "cell-right",
                tdCssWrapper: "cell-right",
            },
        ];

        return tableColConfig;
    }

    private onNavi = (path) => {
        this.props.onClearError();
        Navigator.navigateToAndPreserveQueryParameters(path);
    };

    private renderDocuments() {
        const claccountid = getQueryParameters()["claccountid"];
        const headAccountHierarchyId = getQueryParameters()["hierarchyid"];
        const chargesInformationQuery = DocumentsHelper.getTaskRequestForChargeInformation(
            this.props.data.BatchId,
            claccountid,
            DocumentTaskRequestTypeEnum.ChargesInformation
        );
        const config = [
            {
                title: (
                    <div className="mb-24">
                        <a className="hyperlink">Charges Information</a>
                        <DownloadButton
                            request={chargesInformationQuery}
                            selectedAccount={headAccountHierarchyId}
                        ></DownloadButton>
                    </div>
                ),
                data: " ",
            },
        ];
        return (
            <>
                <Typography type="wider" wrapCss="narrow-margin no-print" config={config}></Typography>
            </>
        );
    }

    public render(): JSX.Element {
        const investmentList = this.props.data.BuyInvestmentList.filter((ele) => ele.OrderPercentage > 0);
        const instrumentCodes: Array<string> = [];
        investmentList.forEach((item, index) => {
            instrumentCodes.push(item.Instrument.ProductCode || item.Instrument.InstrumentCode);
        });
        const { data, type } = this.props;
        const needAlert = hasDecisionMaker(data.SellInvestmentList) || hasDecisionMaker(data.BuyInvestmentList);
        return (
            <>
                {needAlert && <Alert alertType={AlertType.Warning} message={equlityBondWarning} />}
                <h2 className="mb-30">
                    <span>Review</span>
                </h2>
                <CommissionChargeCancelMsg processType={ProcessType.Switch} />
                <div className="d-flex flex-space">
                    <h2 className="mb-11">Order setup</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => {
                            this.props.onClearError();
                            Navigator.navigateTo(Urls.get().SwitchOrderSetup, {
                                scrollAfterNavigating: true,
                                queryParamsObject: {
                                    claccountid: getQueryParameters()["claccountid"],
                                    hierarchyid: getQueryParameters()["hierarchyid"],
                                    subaccounthierarchyid: getQueryParameters()["subaccounthierarchyid"],
                                    batchId: this.props.data.BatchId,
                                },
                            });
                        }}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <OrderSetupReview process={ProcessType.Sell} subAccountType={type} data={data} colNum={2} />

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Switch from investments</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => this.onNavi(Urls.get().SwitchFrom)}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <Table
                    data={data.SellInvestmentList?.filter((ele) => ele.OrderValue > 0)}
                    col={this.getSellTableColumn()}
                    TotalBody={[
                        { data: "Estimated total to switch", colSpan: 3 },
                        {
                            data: MoneyDisplayHelper.FromatMoneyDisplay(
                                data.SellInvestmentList?.reduce((total, ele) => {
                                    return total + (ele.OrderValue ? Number(ele.OrderValue.toFixed(2)) : 0);
                                }, 0),
                                CurrencyCode.GBP,
                                true,
                                2
                            ),
                            tfTdCssWrapper: "cell-right",
                            colSpan: 1,
                        },
                    ]}
                />

                <div className="d-flex flex-space">
                    <h2 className="mb-11">Switch to investments</h2>
                    <button
                        className="edit-circle-icon-button label-sr-only"
                        title="Edit"
                        onClick={() => {
                            this.onNavi(Urls.get().SwitchTo);
                        }}
                    >
                        <span className="icon" aria-hidden="true"></span>
                        <span className="text">Edit</span>
                    </button>
                </div>
                <hr className="heading-underlined-text" />
                <Table
                    data={data.BuyInvestmentList.filter((ele) => ele.OrderPercentage > 0)}
                    col={this.getBuyTableColumn()}
                    TotalBody={[
                        { data: "Total", colSpan: 1 },
                        {
                            data:
                                data.BuyInvestmentList.reduce((total, ele) => {
                                    return Number((total + ele.OrderPercentage).toFixed(2));
                                }, 0) + "%",
                            tfTdCssWrapper: "cell-right",
                            colSpan: 1,
                        },
                    ]}
                />

                <hr className="mt-0" />
                <h3 className="mb-8 h4">Explaining charges to your client</h3>
                <p className="mb-10">
                    It's important that your client understands the costs and charges they will incur based on their
                    investment choices. The Charges Information Document provides the aggregated costs and charges for
                    your client's investment.
                </p>
                <br />
                {this.renderDocuments()}
                <ExternalFundDocument instrumentCodes={instrumentCodes} {...this.props} />
                <div className="no-print">
                    <CheckboxEditor
                        value={data.AdviceGiven}
                        onChange={(v) => {
                            this.props.onChange({ AdviceGiven: v });
                        }}
                        label="Please indicate if advice was given on this trade."
                        cssWrapper="square-checkbox block no-print"
                    ></CheckboxEditor>
                </div>
                {
                    investmentList.some((x) => x.SecurityType == "Cash Product") &&
                    <CashProductDeclarations
                        instrumentCodes={investmentList
                            .filter(x => x.SecurityType === "Cash Product")
                            .map(x => x.InstrumentCode)}
                        isCashDeclarationChecked={this.state.isCashDeclarationChecked}
                        onCashDeclarationChanged={this.handleCashDeclarationChange}
                    />
                }
            </>
        );
    }
}
